import React, { useContext } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { ThemeContext } from '../ThemeContext';
import { useWindowManager } from './WindowManagerContext';
import caseStudies from '../content/caseStudies';

const Container = styled.div`
  padding: 10px;
`;

const Title = styled.h2`
  margin-bottom: 20px;
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};

  .highlight {
    color: var(--highlight-color-light);
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const ProjectCard = styled.div`
  border: 1px solid ${props => props.theme === 'light' ? '#eee' : '#333'};
  border-radius: 8px;
  padding: 15px;
  transition: transform 0.2s, box-shadow 0.2s;
  background-color: ${props => props.theme === 'light' ? 'white' : '#1e1e1e'};
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, ${props => props.theme === 'light' ? '0.1' : '0.3'});
    border-color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  }
`;

const ProjectTitle = styled.h3`
  margin-bottom: 10px;
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
  
  .highlight {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  }
`;

const ProjectDescription = styled.p`
  margin-bottom: 15px;
  color: ${props => props.theme === 'light' ? '#666' : '#b0b0b0'};
  font-size: 0.9rem;
  flex-grow: 1;
`;

const LinksContainer = styled.div`
  margin-top: auto;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ProjectLink = styled.a`
  display: inline-block;
  background-color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  color: ${props => props.theme === 'light' ? 'white' : '#121212'} !important;
  padding: 8px 15px;
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.9rem;
  transition: background-color 0.2s, transform 0.2s;
  
  &:hover {
    background-color: var(--highlight-hover-${props => props.theme === 'light' ? 'light' : 'dark'});
    transform: translateY(-2px);
  }
`;

const InternalProjectLink = styled(Link)`
  display: inline-block;
  background-color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  color: ${props => props.theme === 'light' ? 'white' : '#121212'} !important;
  padding: 8px 15px;
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.9rem;
  transition: background-color 0.2s, transform 0.2s;

  &:hover {
    filter: brightness(90%);
    transform: translateY(-2px);
  }
`;

const ComingSoonTag = styled.span`
  display: inline-block;
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: ${props => props.theme === 'light' ? '#888' : '#999'};
  background: ${props => props.theme === 'light' ? '#f2f2f2' : '#262626'};
  border: 1px dashed ${props => props.theme === 'light' ? '#ccc' : '#444'};
`;

const CaseStudyButton = styled.button`
  display: inline-block;
  background: none;
  border: 1px solid ${props => props.theme === 'light' ? '#ccc' : '#555'};
  color: ${props => props.theme === 'light' ? '#444' : '#bbb'};
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, transform 0.2s;
  font-family: inherit;

  &:hover {
    border-color: var(--accent-color);
    color: var(--accent-color);
    transform: translateY(-2px);
  }
`;

const ProjectsSection = () => {
  const { theme } = useContext(ThemeContext);
  const posthog = usePostHog();
  const { openCaseStudy } = useWindowManager();
  const caseStudySlugs = new Set(caseStudies.map(cs => cs.slug));
  
  const projectsData = [
    {
      id: 'duration-tasks',
      title: 'Power Tools for Google Tasks',
      description: 'A Browser Extension that allows users to set duration for Google Tasks and more.',
      links: [
        { type: 'internal', text: 'Learn more', path: '/duration-for-google-tasks' },
        { type: 'external', text: 'Get it now', path: 'https://chromewebstore.google.com/detail/duration-for-google-tasks/pegfblpefnhgggncfepjeknhhapddebh' }
      ]
    },
    {
      id: 'auth-hell',
      title: 'Authentication Hell',
      description: 'A frustrating unnecessarily complex authentication flow.',
      links: [
        { type: 'internal', text: 'Try it out', path: '/authentication-hell' }
      ]
    },
    {
      id: 'pokedex',
      title: 'Pokedex',
      description: 'A feature-rich Pokedex application. Browse Pokemon, simulate battles, get AI-powered battle advice, and prepare your team for gym challenges.',
      links: [
        { type: 'external', text: 'Check it out', path: 'https://pokedex.xanderj.nl' }
      ]
    },
    {
      id: 'swiper',
      title: 'Swiper',
      description: 'Work in Progress - simplify the thinking process of what to cook tonight.',
      links: []
    }
  ];

  return (
    <Container>
      <Title theme={theme}>Side <span className="highlight">Projects</span></Title>
      <ProjectGrid>
        {projectsData.map(project => (
          <ProjectCard key={project.id} theme={theme}>
            <ProjectTitle theme={theme}>{project.title}</ProjectTitle>
            <ProjectDescription theme={theme}>{project.description}</ProjectDescription>
            <LinksContainer>
              {project.links.map((link, index) => (
                link.type === 'internal' ? (
                  <InternalProjectLink
                    key={index}
                    to={link.path}
                    theme={theme}
                    onClick={() => posthog?.capture('project_link_clicked', { project: project.title, link_text: link.text, url: link.path, type: 'internal' })}
                  >
                    {link.text}
                  </InternalProjectLink>
                ) : (
                  <ProjectLink
                    key={index}
                    href={link.path}
                    theme={theme}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => posthog?.capture('project_link_clicked', { project: project.title, link_text: link.text, url: link.path, type: 'external' })}
                  >
                    {link.text}
                  </ProjectLink>
                )
              ))}
              {caseStudySlugs.has(project.id) && (
                <CaseStudyButton
                  theme={theme}
                  onClick={() => {
                    posthog?.capture('case_study_button_clicked', { project: project.title, slug: project.id });
                    openCaseStudy(project.id, 'projects_window');
                  }}
                >
                  Read case study
                </CaseStudyButton>
              )}
              {project.links.length === 0 && !caseStudySlugs.has(project.id) && (
                <ComingSoonTag theme={theme}>Coming soon</ComingSoonTag>
              )}
            </LinksContainer>
          </ProjectCard>
        ))}
      </ProjectGrid>
    </Container>
  );
};

export default ProjectsSection; 